// src/pages/SalesRecord/components/exportSalesRecordCsv.js
import { formatToCompanyTimezone } from "../../../lib/functions";

const escape = (val) => {
  const str = String(val ?? "");
  return str.includes(",") || str.includes('"') || str.includes("\n")
    ? `"${str.replace(/"/g, '""')}"`
    : str;
};

const money = (v) => Number(v || 0).toFixed(2);
const fmtDate = (v) => (v ? formatToCompanyTimezone(v).split(" ")[0] : "-");

/**
 * @param {object} params
 * @param {Array}   params.dataSource  - the same computed dataSource the table renders
 * @param {object}  params.filters     - filterType + recordType
 * @param {object}  params.authInfo    - for taxLabel + GST columns
 * @param {string}  params.title
 * @param {string}  params.filename
 */
export const exportSalesRecordCsv = ({
  dataSource,
  filters,
  authInfo,
  title    = "Sales Record",
  filename = "sales_record",
}) => {
  const isIndia      = authInfo?.country?.toLowerCase() === "india";
  const isGstEnabled = authInfo?.gst_enabled === "yes";
  const showGst      = isIndia && isGstEnabled;
  const taxLabel     = isIndia ? "GST" : "VAT";
  const { filterType, recordType } = filters;
  const isItemBased  = ["By Item", "By Tag", "By Group", "By Category"].includes(filterType);

  // ── Choose headers + row builder based on mode ────────────────
  let headers = [];
  let buildRow;
  let buildTotalRow;

  if (filterType === "By Group") {
    headers = [
      "No.", "Voucher No", "Date", "Customer Name", "Group Name",
      "Item Name", "Item Code", "Qty", "Rate",
      "Discount%", "Tax%", "Total",
    ];
    buildRow = (row, i) => [
      i + 1, row.sale_voucher_no || "", fmtDate(row.created_date),
      row.acc_name || "", row.group_name || "",
      row.item_name || "", row.display_text || "",
      row.item_qty, money(row.item_rate),
      row.item_discount_per, row.item_tax_per, money(row.item_total),
    ];

  } else if (filterType === "By Category") {
    headers = [
      "No.", "Voucher No", "Date", "Customer Name", "Category Name",
      "Item Name", "Item Code", "Qty", "Rate",
      "Discount%", "Tax%", "Total",
    ];
    buildRow = (row, i) => [
      i + 1, row.sale_voucher_no || "", fmtDate(row.created_date),
      row.acc_name || "", row.category_name || "",
      row.item_name || "", row.display_text || "",
      row.item_qty, money(row.item_rate),
      row.item_discount_per, row.item_tax_per, money(row.item_total),
    ];

  } else if (filterType === "By Item" || filterType === "By Tag") {
    headers = [
      "No.", "Voucher No", "Date", "Customer Name",
      "Tag Group", "Tag Field",
      "Item Name", "Item Code", "Qty", "Rate",
      "Discount%", "Tax%", "Total",
    ];
    buildRow = (row, i) => [
      i + 1, row.sale_voucher_no || "", fmtDate(row.created_date),
      row.acc_name || "",
      row.group_name || "", row.field_name || "",
      row.item_name || "", row.display_text || "",
      row.item_qty, money(row.item_rate),
      row.item_discount_per, row.item_tax_per, money(row.item_total),
    ];

  } else if (recordType === "with item") {
    headers = [
      "No.", "Voucher No", "Date", "Customer Name", "Employee Name",
      "Item Name", "Item Code", "Rate", "Qty",
      "Discount%", "Tax%", "Total",
    ];
    buildRow = (row, i) => [
      i + 1, row.sale_voucher_no || "", fmtDate(row.created_date),
      row.acc_name || "", row.employee_name || "",
      row.item_name || "", row.display_text || "",
      money(row.item_rate), row.item_qty,
      row.item_discount_per, row.item_tax_per, money(row.item_total),
    ];

  } else {
    // without item (summary) — default
    headers = [
      "No.", "Voucher No", "Date", "Customer Name", "Employee Name",
      "Created By", "Mode Of Pay", "Sub Total", "Service Cost",
      "Discount", taxLabel,
      ...(showGst ? ["CGST", "SGST", "IGST"] : []),
      "Total Amount", "Paid", "Due",
    ];
    buildRow = (row, i) => [
      i + 1,
      row.sale_voucher_no || "",
      fmtDate(row.created_date),
      row.acc_name        || "",
      row.employee_name   || "",
      row.full_name       || "",
      row.mode_of_pay     || "",
      money(Number(row.sub_total || 0) + Number(row.total_discount || 0) - Number(row.total_tax || 0)),
      money(row.other_service_cost),
      money(row.total_discount),
      money(row.total_tax),
      ...(showGst ? [
        money(row.cgst_amount),
        money(row.sgst_amount),
        money(row.igst_amount),
      ] : []),
      money(row.total_amount),
      money(row.paid_total),
      money(row.due_total),
    ];
  }

  // ── Build totals row ──────────────────────────────────────────
  if (isItemBased || recordType === "with item") {
    const masterQty = dataSource.reduce((s, r) => s + Number(r.item_qty   || 0), 0);
    const baseQty   = dataSource.reduce((s, r) => s + Number(r.retail_qty || 0), 0);
    const total     = dataSource.reduce((s, r) => s + Number(r.item_total || 0), 0);
    buildTotalRow = () => {
      const row = Array(headers.length).fill("");
      row[0] = "Grand Total:";
      row[headers.length - 4] = `Master Qty: ${money(masterQty)}`;
      row[headers.length - 3] = `Base Qty: ${money(baseQty)}`;
      row[headers.length - 1] = money(total);
      return row;
    };
  } else {
    // without item totals
    buildTotalRow = () => {
      const t = dataSource.reduce((acc, row) => {
        acc.sub_total           += Number(row.sub_total || 0) - Number(row.total_tax || 0);
        acc.other_service_cost  += Number(row.other_service_cost || 0);
        acc.total_discount      += Number(row.total_discount     || 0);
        acc.total_tax           += Number(row.total_tax          || 0);
        acc.cgst                += Number(row.cgst_amount        || 0);
        acc.sgst                += Number(row.sgst_amount        || 0);
        acc.igst                += Number(row.igst_amount        || 0);
        acc.total_amount        += Number(row.total_amount       || 0);
        acc.paid_total          += Number(row.paid_total         || 0);
        acc.due_total           += Number(row.due_total          || 0);
        return acc;
      }, {
        sub_total: 0, other_service_cost: 0, total_discount: 0,
        total_tax: 0, cgst: 0, sgst: 0, igst: 0,
        total_amount: 0, paid_total: 0, due_total: 0,
      });

      return [
        "Grand Total:", "", "", "", "", "", "",
        money(t.sub_total),
        money(t.other_service_cost),
        money(t.total_discount),
        money(t.total_tax),
        ...(showGst ? [money(t.cgst), money(t.sgst), money(t.igst)] : []),
        money(t.total_amount),
        money(t.paid_total),
        money(t.due_total),
      ];
    };
  }

  // ── Assemble rows ─────────────────────────────────────────────
  const rows = [
    [title],
    [],
    headers.map(escape),
    ...dataSource.map((row, i) => buildRow(row, i).map(escape)),
    [],
    buildTotalRow().map(escape),
  ];

  const csv  = rows.map((r) => r.join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};